/* eslint-disable */
// @ts-nocheck

import React, { ReactNode, useState } from 'react'

import ButtonLoading from '@/components/misc/Button'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { Modal, ModalProps } from '../Modal'

interface IStepTabs {
  name: string
  icon?: ReactNode
  disabled?: boolean
  badge?: {
    text: string
    isAnimated?: boolean
    className?: string
  }
  actionButtons: {
    label: string
    secondaryAction?: () => void
    primaryAction?: (data: any) => void
  }[]
  component: ReactNode
}

interface IModalWizardTabsProps extends ModalProps {
  title?: string
  subtitle?: string
  isOpen: boolean
  onClose: Function
  steps: IStepTabs[]
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
  modalClassName?: string
  isSending: boolean
  showDialogAlert: boolean
  confirmOnClose?: boolean
  onTabChange?: (idx: number) => void // nueva prop
}

export function ModalWizardTabs({
  steps,
  isOpen,
  onClose,
  title,
  subtitle,
  size,
  modalClassName,
  isSending,
  showDialogAlert,
  confirmOnClose = false,
  onTabChange,
}: IModalWizardTabsProps) {
  const { useFormWizzard } = useModalWizardTabsContext()
  const [currentStep, setCurrentStep] = useState<IStepTabs>(steps[0])

  const footerComponent = (
    <div className="flex w-full justify-end gap-2">
      <Footer isSending={isSending} step={currentStep} />
    </div>
  )

  return (
    <Modal
      confirmOnClose={confirmOnClose}
      footer={footerComponent}
      isOpen={isOpen}
      modalClassName={modalClassName}
      onClose={onClose}
      showDialogAlert={showDialogAlert}
      size={size}
      subtitle={subtitle}
      title={title}
    >
      <Tabs
        className="flex h-[70vh] w-full flex-col"
        onValueChange={(value) => {
          const idx = steps.findIndex((item) => item.name === value)
          setCurrentStep(steps[idx]!)
          onTabChange && onTabChange(idx)
        }}
        value={currentStep.name}
      >
        <TabsList className="mb-4 flex w-full flex-shrink-0">
          {steps.map((step, idx) => (
            <TabsTrigger
              className="flex w-full items-center gap-1"
              data-value={step.name}
              disabled={step.disabled}
              key={idx}
              value={step.name}
            >
              {step.icon}
              <span>{step.name}</span>
              {step.badge && (
                <Badge
                  aria-hidden="true"
                  className={`absolute -right-2 -top-2 ${step.badge.isAnimated ? 'animate-[ping_0.5s_ease-out_forwards]' : ''} bg-red-500 ${step.badge.className}`}
                >
                  {step.badge.text}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <Form {...useFormWizzard}>
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Content Area */}
            <div className="flex-1 overflow-y-auto px-1 pb-4 scrollbar-thin">
              {currentStep.component}
            </div>
          </div>
        </Form>
      </Tabs>
    </Modal>
  )
}

interface IFooterProps {
  step: IStepTabs
  isSending: boolean
}

function Footer({ step, isSending }: IFooterProps) {
  const { useFormWizzard } = useModalWizardTabsContext()
  const { handleSubmit } = useFormWizzard

  return (
    <>
      {step.actionButtons.map((btn, index) => {
        const isLastButton = index === step.actionButtons.length - 1

        if (isSending) {
          return (
            <ButtonLoading
              key={`${btn.label}-${index}`}
              loading={isSending}
              name="Guardando..."
            />
          )
        }

        return (
          <Button
            disabled={isSending}
            key={`${btn.label}-${index}`}
            onClick={
              btn.primaryAction
                ? handleSubmit(btn.primaryAction)
                : btn.secondaryAction
            }
            variant={isLastButton ? 'default' : 'outline'}
          >
            {btn.label}
          </Button>
        )
      })}
    </>
  )
}
